import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, Loader2, Check, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { captureLead, fullUrl } from "@/lib/api";

const SKILLS = [
  { n: "01", t: "Data & AI Literacy", c: "var(--neon-cyan)" },
  { n: "02", t: "Domain Translation", c: "var(--neon-magenta)" },
  { n: "03", t: "Product & Systems Thinking", c: "var(--volt-blue)" },
  { n: "04", t: "Applied Tooling", c: "var(--neon-cyan)" },
  { n: "05", t: "Portfolio & Proof", c: "var(--neon-magenta)" },
];

export const Freemium = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const { download_url } = await captureLead(email);
      const url = fullUrl(download_url);
      setDone(url);
      window.open(url, "_blank", "noopener");
      toast.success("Your free one-pager is on its way — enjoy!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="free" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <motion.div
        data-testid="freemium-section"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-[2rem] p-8 sm:p-12 lg:p-16 border border-white/10"
        style={{
          background:
            "radial-gradient(120% 120% at 0% 0%, rgba(0,240,255,0.18) 0%, rgba(0,0,0,0) 45%), radial-gradient(120% 120% at 100% 100%, rgba(255,0,228,0.20) 0%, rgba(0,0,0,0) 45%), #0D0B14",
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: copy + form */}
          <div>
            <span className="label-caps px-3 py-1 rounded-full bg-white/10 text-[var(--neon-cyan)] inline-block mb-6">
              Free · No payment
            </span>
            <h2 className="font-future text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight glow-text">
              Top 5 skills to pivot into AI, Fintech &amp; Robotics
            </h2>
            <p className="mt-5 text-white/65 leading-relaxed max-w-lg">
              A one-page field note distilled from The Guide &amp; Workbook — the most transferable
              skills, each with a first move you can make this week. Drop your email and it&apos;s yours.
            </p>

            {done ? (
              <div className="mt-8" data-testid="freemium-success">
                <div className="flex items-center gap-3 text-[var(--neon-cyan)]">
                  <Check size={20} />
                  <span className="font-future">Check your new tab — the PDF is opening.</span>
                </div>
                <a
                  href={done}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="freemium-download-link"
                  className="mt-4 inline-flex items-center gap-2 label-caps px-6 py-3 rounded-full border border-white/25 text-white/80 hover:border-white hover:text-white transition-colors duration-300"
                >
                  <Download size={16} /> Download again
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg">
                <Input
                  data-testid="lead-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30 h-14 flex-1 text-base"
                />
                <button
                  type="submit"
                  data-testid="lead-submit-btn"
                  disabled={loading}
                  className="label-caps h-14 px-7 rounded-xl bg-[var(--neon-cyan)] text-black hover:bg-white transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <>Get the free PDF <ArrowRight size={16} /></>}
                </button>
              </form>
            )}
            <p className="mt-3 font-mono2 text-[11px] text-white/35">
              We&apos;ll only email you about the guides. Unsubscribe anytime · See Privacy Policy.
            </p>
          </div>

          {/* Right: tilted PDF preview */}
          <motion.div
            initial={{ opacity: 0, rotate: -6, y: 30 }}
            whileInView={{ opacity: 1, rotate: -3, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-sm"
          >
            <div className="rounded-2xl bg-[#0A0A0C] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-7">
              <div className="h-1.5 w-full rounded-full mb-5" style={{ background: "linear-gradient(90deg, var(--neon-cyan), var(--neon-magenta))" }} />
              <p className="label-caps text-[var(--neon-cyan)] text-[10px]">Free field note</p>
              <h3 className="font-future text-lg mt-2 leading-tight">Top 5 Skills to Pivot</h3>
              <div className="mt-5 space-y-2.5">
                {SKILLS.map((s) => (
                  <div key={s.n} className="flex items-center gap-3 rounded-lg bg-white/[0.04] px-3 py-2.5">
                    <span className="font-future text-sm" style={{ color: s.c }}>{s.n}</span>
                    <span className="text-sm text-white/80">{s.t}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 h-px w-full bg-white/10" />
              <p className="mt-3 font-mono2 text-[10px] text-white/40">PivoT ransition · pivotransition.ch</p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
